import React, { useState } from 'react';
import { Search, Filter, Plus } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import BreadcrumbNavigation from '../../components/organizational/BreadcrumbNavigation';
import HierarchyTree from '../../components/organizational/HierarchyTree';
import EntityDetailsPane from '../../components/organizational/EntityDetailsPane';
import QuickSettingsDrawer from '../../components/organizational/QuickSettingsDrawer';
import { tenantService } from '../../api/tenantService';
import { organizationService } from '../../api/organizationService';
import { departmentService } from '../../api/departmentService';
import { teamService } from '../../api/teamService';
import { userManagementService } from '../../api/userManagementService';
import toast from 'react-hot-toast';

/**
 * OrganizationalManagement Page
 * Unified page with tree view layout for managing organizational hierarchy
 */
const OrganizationalManagement = () => {
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [hierarchyPath, setHierarchyPath] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerEntity, setDrawerEntity] = useState(null);
  const [drawerEntityType, setDrawerEntityType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const handleEntitySelect = (entity) => {
    setSelectedEntity(entity);
  };

  const handleBreadcrumbNavigate = (path) => {
    setHierarchyPath(path);
    // Find the entity in the path and select it
    if (path.length > 0) {
      const lastEntity = path[path.length - 1];
      // This would need to be implemented to find the entity in the tree
      // For now, just update the path
    }
  };

  const handleEdit = (entity) => {
    setDrawerEntity(entity);
    setDrawerEntityType(entity.type);
    setDrawerOpen(true);
  };

  const handleDelete = async (entity) => {
    if (!window.confirm(`Are you sure you want to delete ${entity.name}?`)) {
      return;
    }

    try {
      switch (entity.type) {
        case 'tenant':
          await tenantService.deleteTenant(entity.data.tenantId);
          break;
        case 'organization':
          await organizationService.deleteOrganization(entity.data.organizationId);
          break;
        case 'department':
          await departmentService.deleteDepartment(entity.data.departmentId);
          break;
        case 'team':
          await teamService.deleteTeam(entity.data.teamId);
          break;
        case 'user':
          await userManagementService.deleteUser(entity.data.userId);
          break;
        default:
          break;
      }
      toast.success('Entity deleted successfully');
      setSelectedEntity(null);
      setHierarchyPath([]);
      // Reload tree would be needed here
    } catch (error) {
      console.error('Error deleting entity:', error);
      toast.error(error.response?.data?.message || 'Failed to delete entity');
    }
  };

  const handleAddChild = (childType) => {
    setDrawerEntity(null);
    setDrawerEntityType(childType);
    setDrawerOpen(true);
  };

  const handleSave = async (formData, entity) => {
    try {
      if (entity) {
        // Update existing entity
        switch (entity.type) {
          case 'tenant':
            await tenantService.updateTenant(entity.data.tenantId, formData);
            break;
          case 'organization':
            await organizationService.updateOrganization(entity.data.organizationId, formData);
            break;
          case 'department':
            await departmentService.updateDepartment(entity.data.departmentId, formData);
            break;
          case 'team':
            await teamService.updateTeam(entity.data.teamId, formData);
            break;
          case 'user':
            await userManagementService.updateUser(entity.data.userId, formData);
            break;
          default:
            break;
        }
      } else {
        // Create new entity
        const parentEntity = selectedEntity;
        
        switch (drawerEntityType) {
          case 'tenant':
            await tenantService.createTenant(formData);
            break;
          case 'organization':
            await organizationService.createOrganization({
              ...formData,
              tenantId: parentEntity?.data?.tenantId
            });
            break;
          case 'department':
            await departmentService.createDepartment({
              ...formData,
              organizationId: parentEntity?.data?.organizationId
            });
            break;
          case 'team':
            await teamService.createTeam({
              ...formData,
              departmentId: parentEntity?.data?.departmentId
            });
            break;
          case 'user':
            await userManagementService.createUser?.(formData); // May need to use signup service
            break;
          default:
            break;
        }
      }
      
      // Reload tree and refresh selected entity
      setSelectedEntity(null);
      setHierarchyPath([]);
      // Tree reload would be needed here
    } catch (error) {
      console.error('Error saving entity:', error);
      throw error;
    }
  };

  const handleCreateNew = () => {
    setDrawerEntity(null);
    setDrawerEntityType('tenant'); // Default to tenant for top-level
    setDrawerOpen(true);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNavigation 
        hierarchy={hierarchyPath}
        onNavigate={handleBreadcrumbNavigate}
      />

      {/* Top Action Bar */}
      <div className="px-6 py-3 border-b bg-white flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search entities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFilterOpen(!filterOpen)}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleCreateNew}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Entity
          </Button>
        </div>
      </div>

      {/* Main Content - Split Pane */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Pane - Tree View */}
        <div className="w-1/4 min-w-[300px] border-r bg-white">
          <HierarchyTree
            selectedEntity={selectedEntity}
            onSelect={handleEntitySelect}
            onNavigate={handleBreadcrumbNavigate}
          />
        </div>

        {/* Right Pane - Entity Details */}
        <div className="flex-1 overflow-hidden">
          <EntityDetailsPane
            entity={selectedEntity}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onAddChild={handleAddChild}
          />
        </div>
      </div>

      {/* Quick Settings Drawer */}
      <QuickSettingsDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerEntity(null);
          setDrawerEntityType(null);
        }}
        entity={drawerEntity}
        entityType={drawerEntityType}
        onSave={handleSave}
      />
    </div>
  );
};

export default OrganizationalManagement;

